/**
 * AgentDetailView.jsx
 * Phase 1: Mitarbeiter-Detail Admin-Dashboard
 *
 * Struktur:
 *   1. Gruppe wählen → Mitarbeiter wählen
 *   2. Zeitraum-Picker
 *   3. Block 1 — KPIs
 *   4. Block 2 — Ticket-Liste
 *   5. Block 3 — Aktueller Stand (Live)
 *   6. Block 4 — Qualität
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import AgentDetailKPIs from './AgentDetailKPIs';
import AgentDetailTickets from './AgentDetailTickets';
import AgentDetailLive from './AgentDetailLive';
import AgentDetailQualitaet from './AgentDetailQualitaet';
import AgentTrendChart from './AgentTrendChart';
import AgentSelfView from './AgentSelfView';
import ZeitraumPicker from './ZeitraumPicker';
import LoadingBar from './LoadingBar';
import './AgentDetailView.css';

const API_BASE = process.env.REACT_APP_API_URL !== undefined ? process.env.REACT_APP_API_URL : 'http://localhost:3001';

export default function AgentDetailView({ auth }) {
  // ─── State ────────────────────────────────────────────────────────────────
  const [gruppen, setGruppen] = useState([]);
  const [selectedGruppe, setSelectedGruppe] = useState('');
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null); // { id, name }
  const [zeitraum, setZeitraum] = useState('heute'); // 'heute'|'woche'|'monat'|'jahr'|{from,to}
  const [showAgentView, setShowAgentView] = useState(false);
  // Refresh-Trigger: erzwingt Re-Fetch auch wenn zeitraum unveraendert (Klick auf Aktualisieren)
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [kpis, setKpis] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);       // 'YYYY-MM-DD' | null
  const [dayTickets, setDayTickets] = useState(null);         // Array | null
  const [dayTicketsLoading, setDayTicketsLoading] = useState(false);
  const [liveStand, setLiveStand] = useState(null);
  const [qualitaet, setQualitaet] = useState(null);

  const [loading, setLoading] = useState({
    gruppen: false, agents: false, kpis: false,
    tickets: false, live: false, qualitaet: false,
  });
  const [errors, setErrors] = useState({});

  const [trend,        setTrend]        = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);

  // ─── Gruppen laden ────────────────────────────────────────────────────────
  useEffect(() => {
    setLoading(l => ({ ...l, gruppen: true }));
    fetch(`${API_BASE}/api/agent-detail/gruppen`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        setGruppen(data.gruppen || []);
      })
      .catch(err => setErrors(e => ({ ...e, gruppen: err.message })))
      .finally(() => setLoading(l => ({ ...l, gruppen: false })));
  }, []);

  // ─── Agenten laden wenn Gruppe wechselt ──────────────────────────────────
  useEffect(() => {
    if (!selectedGruppe) {
      setAgents([]);
      setSelectedAgent(null);
      return;
    }

    setLoading(l => ({ ...l, agents: true }));
    setSelectedAgent(null);
    fetch(`${API_BASE}/api/agent-detail/gruppen/${selectedGruppe}/agents`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        setAgents(data.agents || []);
      })
      .catch(err => setErrors(e => ({ ...e, agents: err.message })))
      .finally(() => setLoading(l => ({ ...l, agents: false })));
  }, [selectedGruppe]);

  // ─── Zeitraum-Query bauen ─────────────────────────────────────────────────
  const buildZeitraumQuery = useCallback((z) => {
    if (typeof z === 'string') return `zeitraum=${z}`;
    if (z && z.from && z.to) return `zeitraum=custom&von=${z.from}&bis=${z.to}`;
    return 'zeitraum=monat';
  }, []);

  // ─── Daten laden wenn Agent oder Zeitraum wechselt ───────────────────────
  // Tickets: ein Fetch mit limit=all — Pagination (Weitere/Alle laden)
  // passiert clientseitig in AgentDetailTickets (gleiches Muster wie der
  // Solver-Drilldown in TicketAnalysisPage).
  //
  // "Aktueller Stand" (/live) bewusst NICHT hier drin: der Endpoint nimmt gar
  // keinen Zeitraum-Parameter (zeigt immer den echten Jetzt-Stand) — würde
  // sonst bei jedem KW/Woche-Wechsel unnötig neu angefragt, obwohl exakt
  // dieselbe Antwort zurückkäme. Eigener Effekt weiter unten (nur Agent-
  // Wechsel + Aktualisieren-Klick).
  // Race-Guard: schnelles Hin-und-Herklicken zwischen Zeitraeumen kann mehrere
  // Requests parallel auslösen — ohne das hier wuerde die ZULETZT ANTWORTENDE
  // (nicht die zuletzt GESTARTETE) Anfrage gewinnen und kurz falsche Daten
  // zeigen. reqIdRef zaehlt hoch, nur die aktuelle ID darf noch schreiben.
  const dataReqIdRef = useRef(0);

  const fetchAllData = useCallback(async (agentId, z) => {
    if (!agentId) return;
    const myReqId = ++dataReqIdRef.current;

    const zq = buildZeitraumQuery(z);
    const base = `${API_BASE}/api/agent-detail/${agentId}`;

    setLoading(l => ({ ...l, kpis: true, tickets: true, qualitaet: true }));
    setErrors(e => ({ ...e, kpis: undefined, tickets: undefined, qualitaet: undefined }));

    const [kpisRes, ticketsRes, qualRes] = await Promise.allSettled([
      fetch(`${base}/kpis?${zq}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${base}/tickets?${zq}&limit=all`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${base}/qualitaet?${zq}`, { credentials: 'include' }).then(r => r.json()),
    ]);

    if (myReqId !== dataReqIdRef.current) return; // veraltete Antwort — verwerfen

    if (kpisRes.status === 'fulfilled') setKpis(kpisRes.value);
    else setErrors(e => ({ ...e, kpis: kpisRes.reason.message }));

    if (ticketsRes.status === 'fulfilled') {
      setTickets(ticketsRes.value.tickets || []);
    } else setErrors(e => ({ ...e, tickets: ticketsRes.reason.message }));

    if (qualRes.status === 'fulfilled') setQualitaet(qualRes.value);
    else setErrors(e => ({ ...e, qualitaet: qualRes.reason.message }));

    setLoading(l => ({ ...l, kpis: false, tickets: false, qualitaet: false }));
  }, [buildZeitraumQuery]);

  useEffect(() => {
    setShowAgentView(false);
    setSelectedDay(null);
    setDayTickets(null);
    if (selectedAgent) {
      fetchAllData(selectedAgent.id, zeitraum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent, zeitraum, refreshTrigger]);

  // "Aktueller Stand" separat: nur bei Agentenwechsel oder "Aktualisieren",
  // nicht bei jedem Zeitraum-Wechsel (siehe Kommentar oben).
  const liveReqIdRef = useRef(0);
  useEffect(() => {
    if (!selectedAgent) { setLiveStand(null); return; }
    const myReqId = ++liveReqIdRef.current;
    setLoading(l => ({ ...l, live: true }));
    setErrors(e => ({ ...e, live: undefined }));
    fetch(`${API_BASE}/api/agent-detail/${selectedAgent.id}/live`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (myReqId === liveReqIdRef.current) setLiveStand(data); })
      .catch(err => { if (myReqId === liveReqIdRef.current) setErrors(e => ({ ...e, live: err.message })); })
      .finally(() => { if (myReqId === liveReqIdRef.current) setLoading(l => ({ ...l, live: false })); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAgent, refreshTrigger]);

  // Das Overlay ist "position: fixed" mit eigenem internen Scroll-Bereich —
  // ohne das hier blieb die dahinterliegende Seite ebenfalls scrollbar
  // (zweite, äussere Scrollbar gleichzeitig mit der inneren des Overlays).
  useEffect(() => {
    if (!showAgentView) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [showAgentView]);

  // ─── Trend laden wenn Agent oder Gruppe wechselt ──────────────────────────
  const trendReqIdRef = useRef(0);
  useEffect(() => {
    if (!selectedAgent || !selectedGruppe) { setTrend(null); return; }
    const myReqId = ++trendReqIdRef.current;
    setTrendLoading(true);
    fetch(`${API_BASE}/api/agent-detail/${selectedAgent.id}/trend?gruppeId=${selectedGruppe}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { if (myReqId === trendReqIdRef.current) setTrend(data); })
      .catch(() => { if (myReqId === trendReqIdRef.current) setTrend(null); })
      .finally(() => { if (myReqId === trendReqIdRef.current) setTrendLoading(false); });
  }, [selectedAgent, selectedGruppe]);

  const handleDayClick = useCallback(async (dayStr) => {
    if (!dayStr || !selectedAgent) {
      setSelectedDay(null);
      setDayTickets(null);
      return;
    }
    setSelectedDay(dayStr);
    setDayTickets(null);
    setDayTicketsLoading(true);
    try {
      // Derselbe Zeitraum wie die Seite, nur zusaetzlich auf den Tag gefiltert.
      // Frueher stand hier ein eigener Ein-Tages-Zeitraum
      // (zeitraum=custom&von=TAG&bis=TAG) — der laedt eine ANDERE Menge als die
      // Tageskarte gezaehlt hat, weil die Monats-Archive nach letzter Bewegung
      // buendeln und ein Ein-Tages-Zeitraum nur einen Monat oeffnet. Gemessen
      // am 24.09.2026: Karte 5, Liste 3 (Misel Zhao, Mo 27.07. in KW31).
      const url = `${API_BASE}/api/agent-detail/${selectedAgent.id}/tickets`
        + `?${buildZeitraumQuery(zeitraum)}&tag=${dayStr}&limit=all`;
      const res = await fetch(url, { credentials: 'include' });
      const data = await res.json();
      setDayTickets(data.tickets || []);
    } catch {
      setDayTickets([]);
    } finally {
      setDayTicketsLoading(false);
    }
  }, [selectedAgent, zeitraum, buildZeitraumQuery]);

  // Refresh-Handler: zeitraum auf 'heute' zuruecksetzen + Re-Fetch erzwingen
  // (auch wenn vorher schon 'heute' aktiv war, via refreshTrigger).
  const handleRefresh = useCallback(() => {
    setZeitraum('heute');
    setRefreshTrigger(r => r + 1);
  }, []);

  // Gesamt-Ladezustand ueber alle zeitraum-/agentenabhaengigen Bloecke —
  // treibt den Top-Ladebalken + sperrt Zeitraum-Picker/Aktualisieren waehrend
  // eine Anfrage laeuft (verhindert ueberlappende Klicks, siehe useApi-Kommentar
  // zum Race-Guard fuer denselben Grund auf Datenebene).
  const anyLoading = loading.kpis || loading.tickets || loading.live || loading.qualitaet || trendLoading;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
    <div className="agent-detail-view">

      <LoadingBar active={anyLoading} />

      {/* ── Selektor: Gruppe + Mitarbeiter ── */}
      <div className="agent-detail-selectors" style={{ position: 'relative' }}>
        <div className="selector-group">
          <label htmlFor="gruppe-select">Gruppe</label>
          <select
            id="gruppe-select"
            value={selectedGruppe}
            onChange={e => setSelectedGruppe(e.target.value)}
            disabled={loading.gruppen}
          >
            <option value="">-- Gruppe wählen --</option>
            {gruppen.map(g => (
              <option key={g.id} value={g.id}>{g.name}</option>
            ))}
          </select>
        </div>

        <div className="selector-group">
          <label htmlFor="agent-select">Mitarbeiter</label>
          <select
            id="agent-select"
            value={selectedAgent?.id || ''}
            onChange={e => {
              const agent = agents.find(a => String(a.id) === e.target.value);
              setSelectedAgent(agent || null);
              setZeitraum('heute'); // bei Mitarbeiter-Wechsel immer auf Heute starten
            }}
            disabled={!selectedGruppe || loading.agents}
          >
            <option value="">-- Mitarbeiter wählen --</option>
            {agents.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        {selectedAgent && (
          <div className={`selector-group${anyLoading ? ' controls-busy' : ''}`}>
            <label>Zeitraum</label>
            <ZeitraumPicker value={zeitraum} onChange={setZeitraum} />
          </div>
        )}

        {selectedAgent && (
          <div className="selector-group selector-group--right">
            <label>&nbsp;</label>
            <button
              type="button"
              className="refresh-btn"
              onClick={handleRefresh}
              title="Aktualisieren — laedt neu und springt auf Heute"
              disabled={anyLoading}
            >
              {anyLoading ? '⏳ Lädt…' : '↻ Aktualisieren'}
            </button>
          </div>
        )}
      </div>

      {/* ── Kein Agent gewählt ── */}
      {!selectedAgent && (
        <div className="agent-detail-empty">
          <p>Bitte wählen Sie eine Gruppe und einen Mitarbeiter aus.</p>
        </div>
      )}

      {/* ── Dashboard ── */}
      {selectedAgent && (
        <div className="agent-detail-dashboard">

          {/* Agent-Header */}
          <div className="agent-header">
            <h2>{selectedAgent.name}</h2>
            {gruppen.find(g => g.id === selectedGruppe)?.name && (
              <span className="agent-gruppe">
                {gruppen.find(g => g.id === selectedGruppe).name}
              </span>
            )}
            <button
              className="agent-view-toggle-btn"
              onClick={() => setShowAgentView(true)}
              title="Agenten-Ansicht anzeigen"
            >
              👁 Agenten-Ansicht
            </button>
          </div>

          {/* Block 1 — KPIs */}
          <section className="dashboard-block">
            <h3>Kennzahlen</h3>
            {errors.kpis && <ErrorBanner message={errors.kpis} />}
            <AgentDetailKPIs
              data={kpis}
              loading={loading.kpis}
              selectedDay={selectedDay}
              onDayClick={handleDayClick}
            />
          </section>

          {/* Block 2 — Ticket-Liste */}
          <section className="dashboard-block">
            <h3>
              {selectedDay
                ? (() => {
                    const tag = kpis?.tage?.find(t => t.dayStr === selectedDay);
                    return `Tickets — ${tag ? `${tag.wochentag} ${tag.datum}` : selectedDay}`;
                  })()
                : 'Tickets im Zeitraum'
              }
            </h3>
            {errors.tickets && <ErrorBanner message={errors.tickets} />}
            <AgentDetailTickets
              tickets={selectedDay ? (dayTickets || []) : tickets}
              loading={selectedDay ? dayTicketsLoading : loading.tickets}
              agentId={selectedAgent.id}
              zeitraum={zeitraum}
              buildZeitraumQuery={buildZeitraumQuery}
              apiBase={API_BASE}
              total={selectedDay ? (dayTickets?.length ?? 0) : tickets.length}
            />
          </section>

          {/* Block 3 — Aktueller Stand */}
          <section className="dashboard-block">
            <h3>Aktueller Stand</h3>
            {errors.live && <ErrorBanner message={errors.live} />}
            <AgentDetailLive data={liveStand} loading={loading.live} />
          </section>

          {/* Block 4 — Qualität */}
          <section className="dashboard-block">
            <h3>Qualität</h3>
            {errors.qualitaet && <ErrorBanner message={errors.qualitaet} />}
            <AgentDetailQualitaet data={qualitaet} loading={loading.qualitaet} agentId={selectedAgent?.id} />
          </section>

          {/* Block 5 — Jahresverlauf */}
          <section className="dashboard-block">
            <h3>Jahresverlauf — Vergleich mit Team</h3>
            <AgentTrendChart data={trend} loading={trendLoading} />
          </section>

        </div>
      )}
    </div>

    {/* ── Agenten-Ansicht Overlay ── */}
    {showAgentView && selectedAgent && (
      <div className="agent-view-overlay">
        <div className="agent-view-overlay-header">
          <button
            className="agent-view-back-btn"
            onClick={() => setShowAgentView(false)}
          >
            ← Admin-Ansicht
          </button>
          <span className="agent-view-overlay-title">
            {selectedAgent.name} — Agenten-Ansicht
          </span>
        </div>
        <AgentSelfView agentId={selectedAgent.id} agentName={selectedAgent.name} />
      </div>
    )}
    </>
  );
}

function ErrorBanner({ message }) {
  return (
    <div className="error-banner">
      <span>⚠ {message}</span>
    </div>
  );
}
